import { Node as ProseNode } from 'prosemirror-model'
import { Decoration, EditorView } from 'prosemirror-view'
import { createReactNodeView } from '../ReactNodeView/ReactNodeView'
import { useReactNodeViewPortals } from '../ReactNodeView/ReactnodeViewPortalsContext'

type portalType = Pick<ReturnType<typeof useReactNodeViewPortals>, 'createPortal' | 'removePortal'>

/**
 * A factory function that when invoked with `createPortal` and `removePortal`, returns a function that can be used
 * to create ReactNodeViews with less boilerplate.
 * @param onCreatePortal - callback that is called when the portal is created
 * @param onDestroyPortal - callback that is called when the portal is destroyed
 * @param inCommentMode - boolean whether the editor is in comment mode
 * @returns A factory function, returning a function that creates a ReactNodeView
 * @example
 * ```typescript
 * const makeReactNodeViewFn = reactNodeViewFactory(createPortal, removePortal, inCommentMode)
 *
 * const editorProps: EditorProps = {
 *   nodeViews: {
 *     card: makeReactNodeViewFn(Card),
 *   }
 * }
 * ```
 */
export const reactNodeViewFactory =
  (onCreatePortal: portalType['createPortal'], onDestroyPortal: portalType['removePortal'], inCommentMode: boolean) =>
  (component: React.FC<React.PropsWithChildren<any>>) =>
  (node: ProseNode, view: EditorView, getPos: () => number, decorations: Decoration[]) =>
    createReactNodeView({
      node,
      view,
      getPos,
      decorations,
      component,
      onCreatePortal,
      onDestroyPortal,
      inCommentMode,
    })
